import type { BoxProps } from '@mui/material';
import { Box, styled } from '@mui/material';

/**
 * Main container for the table with scroll capabilities
 * Optimized for smooth sticky positioning without jitter
 */
export const TableContainer: React.ComponentType<BoxProps> = styled(Box)(() => ({
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    position: 'relative',
    maxWidth: '100vw',
    // Hardware acceleration for smooth scrolling
    WebkitOverflowScrolling: 'touch',
    msOverflowStyle: '-ms-autohiding-scrollbar',
    touchAction: 'pan-x pan-y',
    overscrollBehaviorX: 'contain',
    // Remove scroll snap to avoid sticky jitter
    // scrollSnapType: 'x proximity',
    // Remove all transforms to prevent sticky conflicts
    // Smooth scrolling behavior
    scrollBehavior: 'auto',
    // Prevent subpixel rendering issues
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
}));

/**
 * Styled table component using flexbox layout
 * Optimized for stable sticky positioning
 */
export const StyledTable: React.ComponentType<React.HTMLAttributes<HTMLDivElement>> = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: 'max-content',
    minWidth: '100%',
    // Minimal styling to prevent sticky conflicts
});

/**
 * Sticky header for the table
 * Optimized to prevent jitter during scroll
 */
export const TableHeader: React.ComponentType<React.HTMLAttributes<HTMLDivElement>> = styled('div')({
    display: 'flex',
    position: 'sticky',
    top: 0,
    zIndex: 12, // Higher than frozen cells (11) to stay above them during scroll
    // Remove transforms to prevent sticky jitter
    // Prevent subpixel issues
    WebkitBackfaceVisibility: 'hidden',
    backfaceVisibility: 'hidden',
    // Ensure crisp rendering
    WebkitFontSmoothing: 'antialiased',
});

/**
 * Body container for table rows
 */
export const TableBody: React.ComponentType<React.HTMLAttributes<HTMLDivElement>> = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

/**
 * Individual table row with category color support
 */
export const TableRow: React.ComponentType<React.HTMLAttributes<HTMLDivElement> & { categorycolor?: string }> = styled('div')<{ categoryColor?: string }>(({ categoryColor }) => ({
    display: 'flex',
    position: 'relative',
    backgroundColor: categoryColor || 'transparent',
}));

/**
 * Container for category header with collapse/expand functionality
 */
export const CategoryHeader: React.ComponentType<BoxProps & { categorycolor?: string }> = styled(Box)<{ categorycolor?: string }>(({ theme, categorycolor }) => ({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: categorycolor 
        ? categorycolor.replace('rgb', 'rgba').replace(')', ', 1)')
        : theme.palette.grey[200],
}));

/**
 * Sticky section for category header content
 * Optimized for smooth sticky positioning
 */
export const CategoryHeaderSticky: React.ComponentType<BoxProps> = styled(Box)({
    position: 'sticky',
    left: 0,
    top: 0,
    display: 'flex',
    alignItems: 'center',
    zIndex: 10,
    backgroundColor: 'inherit',
    height: '100%',
    padding: '8px 16px',
    // Stable sticky positioning without transforms
    isolation: 'isolate',
    // Prevent rendering issues
    WebkitBackfaceVisibility: 'hidden',
    backfaceVisibility: 'hidden',
    // Smooth font rendering
    WebkitFontSmoothing: 'antialiased',
    // Force integer pixel positioning
    WebkitTransformStyle: 'preserve-3d',
    transformStyle: 'preserve-3d',
});
